/**
 * The LexiStar maintenance API (ST-00249): maintenance and delay codes, maintenance / breakdown /
 * delay events per truck, and the time model. Shapes: `types/lexistar-maintenance`. Server:
 * server/modules/lexistar/maintenance, mounted at /api/lexistar.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response BODY.
 *
 *   listCodes(query)               → { codes, seeded }  (an organisation's first read seeds the defaults)
 *   createCode(input)              → code
 *   updateCode(id, patch)          → code               (label, category — new events only —, active, sort)
 *   reorderCodes(ids)              → { codes }
 *   listEvents(query)              → { events, has_more, now }
 *   createEvent(input)             → event              (book / start now / log a past one)
 *   startEvent / extendEvent / closeEvent / cancelEvent(id, body) → event
 *   getTimeModel(query)            → { assets: [{ buckets, totals, segments? }] }
 */
import api from './api';
import type {
  EventCategory,
  MaintenanceCode,
  MaintenanceCodeInput,
  MaintenanceCodeList,
  MaintenanceCodePatch,
  TimeEvent,
  TimeEventInput,
  TimeEventList,
  TimeGrain,
  TimeModelResponse,
} from '../types/lexistar-maintenance';

const BASE = '/lexistar/maintenance';
const event = (id: string): string => `${BASE}/events/${encodeURIComponent(id)}`;

/** Query parameters without the empty ones; arrays as comma lists. */
function params(q: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
    } else out[k] = String(v);
  }
  return out;
}

export async function listCodes(
  query: { include_inactive?: boolean; category?: EventCategory; tenantId?: number } = {}
): Promise<MaintenanceCodeList> {
  const res = await api.get(`${BASE}/codes`, { params: params(query) });
  return res.data as MaintenanceCodeList;
}

export async function createCode(input: MaintenanceCodeInput): Promise<MaintenanceCode> {
  const res = await api.post(`${BASE}/codes`, input);
  return (res.data as { code: MaintenanceCode }).code;
}

export async function updateCode(id: string, patch: MaintenanceCodePatch): Promise<MaintenanceCode> {
  const res = await api.patch(`${BASE}/codes/${encodeURIComponent(id)}`, patch);
  return (res.data as { code: MaintenanceCode }).code;
}

export async function reorderCodes(ids: string[], tenantId?: number | null): Promise<MaintenanceCodeList> {
  const res = await api.post(`${BASE}/codes:reorder`, { ids, ...(tenantId ? { tenant_id: tenantId } : {}) });
  return res.data as MaintenanceCodeList;
}

export interface EventsQuery {
  asset_id?: string[];
  category?: EventCategory[];
  /** booked | open | closed | cancelled | active (= booked or open). */
  state?: string[];
  /** A SAST day (YYYY-MM-DD) or an ISO instant. */
  from?: string;
  to?: string;
  code_id?: string;
  limit?: number;
}

export async function listEvents(query: EventsQuery = {}): Promise<TimeEventList> {
  const res = await api.get(`${BASE}/events`, { params: params(query as Record<string, unknown>) });
  return res.data as TimeEventList;
}

export async function createEvent(input: TimeEventInput): Promise<TimeEvent> {
  const res = await api.post(`${BASE}/events`, input);
  return (res.data as { event: TimeEvent }).event;
}

export async function startEvent(id: string, body: { at?: string; expected_end_at?: string | null; version?: number } = {}): Promise<TimeEvent> {
  const res = await api.post(`${event(id)}:start`, body);
  return (res.data as { event: TimeEvent }).event;
}

export async function extendEvent(id: string, body: { expected_end_at: string; notes?: string | null; version?: number }): Promise<TimeEvent> {
  const res = await api.post(`${event(id)}:extend`, body);
  return (res.data as { event: TimeEvent }).event;
}

export async function closeEvent(id: string, body: { ends_at?: string; notes?: string | null; version?: number } = {}): Promise<TimeEvent> {
  const res = await api.post(`${event(id)}:close`, body);
  return (res.data as { event: TimeEvent }).event;
}

export async function cancelEvent(id: string, body: { reason: string; version?: number }): Promise<TimeEvent> {
  const res = await api.post(`${event(id)}:cancel`, body);
  return (res.data as { event: TimeEvent }).event;
}

export async function getTimeModel(query: {
  asset?: string[];
  from?: string;
  to?: string;
  grain?: TimeGrain;
  segments?: boolean;
}): Promise<TimeModelResponse> {
  const res = await api.get('/lexistar/time-model', { params: params(query) });
  return res.data as TimeModelResponse;
}

/** The server's message of a failed call (validation details joined), else `fallback`. */
export function errorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string; message?: string; details?: string[] } } })?.response?.data;
  if (data?.details?.length) return data.details.join('; ');
  return data?.error || data?.message || fallback;
}

/** The clashing event of a 409 `overlap`, if that is what failed. */
export function overlapOf(err: unknown): TimeEvent | null {
  const data = (err as { response?: { status?: number; data?: { code?: string; conflict?: TimeEvent } } })?.response;
  return data?.status === 409 && data.data?.code === 'overlap' ? data.data.conflict || null : null;
}
